/**
 * Main map view component
 */

import React, { useState } from 'react';
import MapContainer from './MapContainer';
import CountrySidebar from '../sidebar/CountrySidebar';
import { useAppContext } from '../../context/AppContext';
import '../../styles/components/map/MapView.css';

/**
 * Map view component with resizable sidebar
 * @returns {JSX.Element} MapView component
 */
const MapView = () => {
  // State for sidebar (selectedCountry now comes from context)
  const [sidebarWidth, setSidebarWidth] = useState(20);
  const [isResizing, setIsResizing] = useState(false);
  
  // Get app context data
  const { loading, error, selectedCountry, setSelectedCountry } = useAppContext();

  // Handle sidebar resize
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
    
    const startX = e.clientX;
    const startWidth = sidebarWidth;
    
    const onMouseMove = (e) => {
      const container = document.querySelector('.content-container');
      if (!container) return;
      
      const containerRect = container.getBoundingClientRect();
      const containerWidth = containerRect.width;
      
      const deltaX = startX - e.clientX;
      const deltaPercent = (deltaX / containerWidth) * 100;
      const newWidth = Math.max(20, Math.min(60, startWidth + deltaPercent));
      
      setSidebarWidth(newWidth);
    };
    
    const onMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
    
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
  };

  // Handle sidebar close
  const handleCloseSidebar = () => {
    setSelectedCountry(null);
  };

  // Calculate map width based on sidebar
  const mapWidth = selectedCountry ? (100 - sidebarWidth) : 100;

  // Render loading state
  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  // Render error state
  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  return (
    <div className="content-container">
      <div 
        className="map-container"
        style={{ 
          width: `${mapWidth}%`,
          transition: isResizing ? 'none' : 'width 0.3s ease'
        }}
      >
        <MapContainer onCountrySelect={setSelectedCountry} />
      </div>
      
      {selectedCountry && (
        <>
          <div 
            className="resize-handle"
            onMouseDown={handleMouseDown}
            style={{ 
              backgroundColor: isResizing ? '#0074D9' : '#ddd'
            }}
          >
            <div 
              className="resize-line"
              style={{
                backgroundColor: isResizing ? 'white' : '#999'
              }}
            ></div>
          </div>
          
          <div 
            className="sidebar-container"
            style={{ 
              width: `${sidebarWidth}%`,
              transition: isResizing ? 'none' : 'width 0.3s ease'
            }}
          >
            <CountrySidebar 
              country={selectedCountry}
              onClose={handleCloseSidebar}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default MapView;
